import type {
  CatalogueLinkAccess,
  CatalogueLinkExtensionRequest,
  CatalogueShareLink,
  CatalogueViewerGate,
  CatalogueViewerOtpChannel,
  CatalogueViewerOtpSendPayload,
  CatalogueViewerSessionPayload,
  CreateCatalogueShareLinkRequest,
  PublicCatalogueStatus,
  SetCatalogueShareLinkPasswordRequest,
} from "../../types/catalogue.types";
import {
  buildPublicAuthHeaders,
  buildPublicJsonHeaders,
} from "../../pages/catalog/ecomerce/publicCatalogueHelpers";

type ApiEnvelope<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

const securityPath = (catalogueId: string, suffix = ""): string => {
  const base = (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");
  return `${base}/catalogue-security/${encodeURIComponent(catalogueId)}${suffix}`;
};

/**
 * Buyer-page status for a catalogue (and invite link). Sends the studio JWT when signed in: the
 * server answers viewerGate NONE only for the studio that owns the catalogue.
 */
export async function fetchPublicCatalogueStatus(
  catalogueId: string,
  linkToken?: string
): Promise<ApiEnvelope<PublicCatalogueStatus>> {
  const query = linkToken ? `?link=${encodeURIComponent(linkToken)}` : "";
  const res = await fetch(securityPath(catalogueId, `/public-status${query}`), {
    headers: buildPublicAuthHeaders({ includeStudioJwt: true }),
  });
  return readEnvelope<PublicCatalogueStatus>(res);
}

export function studioCatalogueAuthHeaders(): HeadersInit {
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : "";
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token || ""}`,
  };
}

async function readEnvelope<T>(res: Response): Promise<ApiEnvelope<T>> {
  const text = await res.text();
  if (!text) {
    return { success: res.ok, message: res.ok ? undefined : `Request failed (HTTP ${res.status}).` };
  }
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    return {
      success: false,
      message: res.ok ? "Unexpected response from server." : `Request failed (HTTP ${res.status}).`,
    };
  }
}

export async function createCatalogueShareLink(
  catalogueId: string,
  body: CreateCatalogueShareLinkRequest
): Promise<ApiEnvelope<CatalogueShareLink>> {
  const payload: CreateCatalogueShareLinkRequest = {
    recipientName: body.recipientName,
    recipientPhone: body.recipientPhone,
    recipientCountryCode: body.recipientCountryCode,
    expiryAmount: body.expiryAmount,
    expiryUnit: body.expiryUnit,
  };
  if (body.recipientEmail) payload.recipientEmail = body.recipientEmail;
  if (body.sendInviteEmail !== undefined) payload.sendInviteEmail = body.sendInviteEmail;
  if (body.sendWhatsApp !== undefined) payload.sendWhatsApp = body.sendWhatsApp;
  if (body.generateAccessPassword) payload.generateAccessPassword = true;
  else if (body.linkPassword) payload.linkPassword = body.linkPassword;
  if (body.clientTier) payload.clientTier = body.clientTier;
  // Reseller sharing (ignored by the server while its flag is off).
  if (body.accessMode) payload.accessMode = body.accessMode;
  if (body.linkRole) payload.linkRole = body.linkRole;
  if (body.resellerCustomerId) payload.resellerCustomerId = body.resellerCustomerId;
  if (body.brandMode) payload.brandMode = body.brandMode;
  if (body.priceTier) payload.priceTier = body.priceTier;
  if (body.viewerGate) payload.viewerGate = body.viewerGate;
  if (body.markReseller) {
    payload.markReseller = true;
    if (body.resellerForwardLimit) payload.resellerForwardLimit = body.resellerForwardLimit;
  }
  const res = await fetch(securityPath(catalogueId, "/links"), {
    method: "POST",
    headers: studioCatalogueAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return readEnvelope<CatalogueShareLink>(res);
}

export async function listCatalogueShareLinks(
  catalogueId: string
): Promise<ApiEnvelope<CatalogueShareLink[]>> {
  const res = await fetch(securityPath(catalogueId, "/links"), {
    headers: studioCatalogueAuthHeaders(),
  });
  return readEnvelope<CatalogueShareLink[]>(res);
}

/** What a new link starts with: the catalogue's last studio link, or standard values for a first link. */
export type CatalogueShareLinkDefaults = {
  access: "OPEN" | "EXCLUSIVE" | "RESELLER";
  /** null = product default prices. */
  priceTier: string | null;
  viewerGate: CatalogueViewerGate;
  expiryAmount: number;
  expiryUnit: "minutes" | "hours" | "days";
  /** When the link these came from was made; null for a first link. */
  fromLinkAt: string | null;
};

export async function fetchCatalogueShareLinkDefaults(
  catalogueId: string
): Promise<ApiEnvelope<CatalogueShareLinkDefaults>> {
  const res = await fetch(securityPath(catalogueId, "/link-defaults"), {
    headers: studioCatalogueAuthHeaders(),
  });
  return readEnvelope<CatalogueShareLinkDefaults>(res);
}

export async function setCatalogueShareLinkAccessPassword(
  catalogueId: string,
  linkId: string,
  body: SetCatalogueShareLinkPasswordRequest
): Promise<ApiEnvelope<CatalogueShareLink>> {
  const payload: SetCatalogueShareLinkPasswordRequest = {};
  if (body.generateAccessPassword) payload.generateAccessPassword = true;
  else if (body.linkPassword) payload.linkPassword = body.linkPassword;
  if (body.sendInviteEmail !== undefined) payload.sendInviteEmail = body.sendInviteEmail;
  const res = await fetch(
    securityPath(catalogueId, `/links/${encodeURIComponent(linkId)}/access-password`),
    {
      method: "PUT",
      headers: studioCatalogueAuthHeaders(),
      body: JSON.stringify(payload),
    }
  );
  return readEnvelope<CatalogueShareLink>(res);
}

/** Which price tier one link shows. Empty = the catalogue's default prices. */
export async function setCatalogueShareLinkPriceTier(
  catalogueId: string,
  linkId: string,
  priceTier: string
): Promise<ApiEnvelope<CatalogueShareLink>> {
  const res = await fetch(
    securityPath(catalogueId, `/links/${encodeURIComponent(linkId)}/price-tier`),
    {
      method: "PUT",
      headers: studioCatalogueAuthHeaders(),
      body: JSON.stringify({ priceTier }),
    }
  );
  return readEnvelope<CatalogueShareLink>(res);
}

/** Switch one link between Open and Exclusive. Other links on the catalogue keep theirs. */
export async function setCatalogueShareLinkAccessMode(
  catalogueId: string,
  linkId: string,
  accessMode: CatalogueLinkAccess
): Promise<ApiEnvelope<CatalogueShareLink>> {
  const res = await fetch(
    securityPath(catalogueId, `/links/${encodeURIComponent(linkId)}/access-mode`),
    {
      method: "PUT",
      headers: studioCatalogueAuthHeaders(),
      body: JSON.stringify({ accessMode }),
    }
  );
  return readEnvelope<CatalogueShareLink>(res);
}

/** What viewers of one Open link give before it opens. Other links keep theirs. */
export async function setCatalogueShareLinkViewerGate(
  catalogueId: string,
  linkId: string,
  viewerGate: CatalogueViewerGate
): Promise<ApiEnvelope<CatalogueShareLink>> {
  const res = await fetch(
    securityPath(catalogueId, `/links/${encodeURIComponent(linkId)}/viewer-gate`),
    {
      method: "PUT",
      headers: studioCatalogueAuthHeaders(),
      body: JSON.stringify({ viewerGate }),
    }
  );
  return readEnvelope<CatalogueShareLink>(res);
}

/**
 * SMS code for a gated group link, to the viewer's own mobile. `data.otpAvailable === false` means
 * the number cannot get an SMS (not Indian) and the viewer continues without a code.
 */
export async function sendCatalogueGateOtp(
  catalogueId: string,
  body: { linkToken: string; phone: string; countryCode?: string }
): Promise<ApiEnvelope<{ maskedPhone?: string; smsSent?: boolean; otpAvailable?: boolean }>> {
  const payload: Record<string, string> = { linkToken: body.linkToken, phone: body.phone };
  if (body.countryCode) payload.countryCode = body.countryCode;
  const res = await fetch(securityPath(catalogueId, "/send-gate-otp"), {
    method: "POST",
    headers: buildPublicJsonHeaders({ includeStudioJwt: true }),
    body: JSON.stringify(payload),
  });
  return readEnvelope<{ maskedPhone?: string; smsSent?: boolean; otpAvailable?: boolean }>(res);
}

export async function emailCatalogueShareInvite(
  catalogueId: string,
  linkId: string
): Promise<ApiEnvelope<CatalogueShareLink>> {
  const res = await fetch(
    securityPath(catalogueId, `/links/${encodeURIComponent(linkId)}/email-invite`),
    {
      method: "POST",
      headers: studioCatalogueAuthHeaders(),
    }
  );
  return readEnvelope<CatalogueShareLink>(res);
}

export async function disableCatalogueShareLink(
  catalogueId: string,
  linkId: string
): Promise<ApiEnvelope<null>> {
  const res = await fetch(
    securityPath(catalogueId, `/links/${encodeURIComponent(linkId)}/disable`),
    {
      method: "PUT",
      headers: studioCatalogueAuthHeaders(),
    }
  );
  return readEnvelope<null>(res);
}

/** Set when one link expires, counted from now: shorter or longer than it was. */
export async function setCatalogueShareLinkExpiry(
  catalogueId: string,
  linkId: string,
  expiry: { expiryAmount: number; expiryUnit: string }
): Promise<ApiEnvelope<CatalogueShareLink>> {
  const res = await fetch(
    securityPath(catalogueId, `/links/${encodeURIComponent(linkId)}/expiry`),
    {
      method: "PUT",
      headers: studioCatalogueAuthHeaders(),
      body: JSON.stringify(expiry),
    }
  );
  return readEnvelope<CatalogueShareLink>(res);
}

export async function extendCatalogueShareLinkExpiry(
  catalogueId: string,
  linkId: string,
  days: number
): Promise<ApiEnvelope<CatalogueShareLink>> {
  const res = await fetch(
    securityPath(catalogueId, `/links/${encodeURIComponent(linkId)}/extend-expiry`),
    {
      method: "POST",
      headers: studioCatalogueAuthHeaders(),
      body: JSON.stringify({ days }),
    }
  );
  return readEnvelope<CatalogueShareLink>(res);
}

/** Buyer on an expired link asks for it to reopen. status: SENT, ALREADY_SENT or DECLINED. */
export async function requestCatalogueLinkExtension(
  catalogueId: string,
  linkToken: string,
  body: { name?: string; phone?: string }
): Promise<ApiEnvelope<{ status: "SENT" | "ALREADY_SENT" | "DECLINED" }>> {
  const res = await fetch(securityPath(catalogueId, `/extension-request?link=${encodeURIComponent(linkToken)}`), {
    method: "POST",
    headers: buildPublicJsonHeaders(),
    body: JSON.stringify(body),
  });
  return readEnvelope(res);
}

export async function listCatalogueLinkExtensionRequests(
  catalogueId: string
): Promise<ApiEnvelope<CatalogueLinkExtensionRequest[]>> {
  const res = await fetch(securityPath(catalogueId, "/extension-requests"), {
    headers: studioCatalogueAuthHeaders(),
  });
  return readEnvelope<CatalogueLinkExtensionRequest[]>(res);
}

/** Reopen the same link for 7, 30 or 90 days. */
export async function approveCatalogueLinkExtension(
  catalogueId: string,
  requestId: string,
  days: 7 | 30 | 90
): Promise<ApiEnvelope<CatalogueShareLink>> {
  const res = await fetch(securityPath(catalogueId, `/extension-requests/${encodeURIComponent(requestId)}/approve`), {
    method: "POST",
    headers: studioCatalogueAuthHeaders(),
    body: JSON.stringify({ days }),
  });
  return readEnvelope<CatalogueShareLink>(res);
}

export async function declineCatalogueLinkExtension(
  catalogueId: string,
  requestId: string
): Promise<ApiEnvelope<null>> {
  const res = await fetch(securityPath(catalogueId, `/extension-requests/${encodeURIComponent(requestId)}/decline`), {
    method: "POST",
    headers: studioCatalogueAuthHeaders(),
  });
  return readEnvelope<null>(res);
}

export async function fetchCataloguePublicStatus(
  catalogueId: string,
  linkToken?: string
): Promise<ApiEnvelope<PublicCatalogueStatus>> {
  const suffix = linkToken
    ? `/public-status?link=${encodeURIComponent(linkToken)}`
    : "/public-status";
  const res = await fetch(securityPath(catalogueId, suffix), {
    headers: buildPublicJsonHeaders(),
  });
  return readEnvelope<PublicCatalogueStatus>(res);
}

export async function generateCatalogueViewerSession(
  catalogueId: string,
  body: {
    linkToken: string;
    password: string;
    phone?: string;
    countryCode?: string;
    email?: string;
  }
): Promise<ApiEnvelope<CatalogueViewerSessionPayload>> {
  const payload: Record<string, string> = {
    linkToken: body.linkToken,
    password: body.password,
  };
  if (body.phone) payload.phone = body.phone;
  if (body.countryCode) payload.countryCode = body.countryCode;
  if (body.email) payload.email = body.email;
  const res = await fetch(securityPath(catalogueId, "/generate-session"), {
    method: "POST",
    headers: buildPublicJsonHeaders({ includeStudioJwt: true }),
    body: JSON.stringify(payload),
  });
  return readEnvelope<CatalogueViewerSessionPayload>(res);
}

export async function sendCatalogueViewerOtp(
  catalogueId: string,
  body: {
    linkToken: string;
    channel?: CatalogueViewerOtpChannel;
    phone?: string;
    countryCode?: string;
    email?: string;
  }
): Promise<ApiEnvelope<CatalogueViewerOtpSendPayload>> {
  const payload: Record<string, string> = {
    linkToken: body.linkToken,
    channel: body.channel || "auto",
  };
  if (body.phone) payload.phone = body.phone;
  if (body.countryCode) payload.countryCode = body.countryCode;
  if (body.email) payload.email = body.email;
  const res = await fetch(securityPath(catalogueId, "/send-viewer-otp"), {
    method: "POST",
    headers: buildPublicJsonHeaders({ includeStudioJwt: true }),
    body: JSON.stringify(payload),
  });
  return readEnvelope<CatalogueViewerOtpSendPayload>(res);
}

export async function verifyCatalogueViewerOtp(
  catalogueId: string,
  body: {
    linkToken: string;
    otp: string;
    phone?: string;
    countryCode?: string;
    email?: string;
  }
): Promise<ApiEnvelope<CatalogueViewerSessionPayload>> {
  const payload: Record<string, string> = {
    linkToken: body.linkToken,
    otp: body.otp,
  };
  if (body.phone) payload.phone = body.phone;
  if (body.countryCode) payload.countryCode = body.countryCode;
  if (body.email) payload.email = body.email;
  const res = await fetch(securityPath(catalogueId, "/verify-viewer-otp"), {
    method: "POST",
    headers: buildPublicJsonHeaders({ includeStudioJwt: true }),
    body: JSON.stringify(payload),
  });
  return readEnvelope<CatalogueViewerSessionPayload>(res);
}
